import React, { useCallback, useMemo } from 'react';
import type { SorterResult } from 'antd/es/table/interface';

import { AppPagination, AppTable } from '@/components/atoms';
import type { ClinicUserListItem } from '@/services/admin/clinicUsers';
import { Empty } from 'antd';
import { EMPTY_TEXT_FILTERED } from '@/constants';

type ClinicUsersTableSectionProps = {
  rows: ClinicUserListItem[];
  loading: boolean;
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  defaultPerPage: number;
  /** When true (super-admin or family admin), show サイト区分 column. */
  canUseSiteTypeFeatures: boolean;
  tenantNameBySiteType: Map<string, string>;
  sortKey: string | null;
  sortOrder: 'ascend' | 'descend' | null;
  onSort: (key: string | null, order: 'ascend' | 'descend' | null) => void;
  onChangePage: (page: number) => void;
};

const ClinicUsersTableSection: React.FC<ClinicUsersTableSectionProps> = ({
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures,
  tenantNameBySiteType,
  sortKey,
  sortOrder,
  onSort,
  onChangePage,
}) => {
  const handleTableChange = useCallback(
    (
      _p: any,
      _f: any,
      sorter: SorterResult<ClinicUserListItem> | SorterResult<ClinicUserListItem>[]
    ) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      onSort(s.order ? (s.columnKey as string) : null, s.order ?? null);
    },
    [onSort]
  );

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        key: 'display_id',
        dataIndex: 'display_id',
        width: 60,
      },
      ...(canUseSiteTypeFeatures
        ? [
            {
              title: 'サイト区分',
              key: 'site_type',
              dataIndex: 'site_type',
              width: 150,
              render: (siteType: string | undefined) =>
                (siteType && tenantNameBySiteType.get(siteType)) || siteType || '-',
            },
          ]
        : []),
      {
        title: '名前',
        key: 'name',
        dataIndex: 'name',
        sorter: true,
        tooltip: null,
        sortOrder: sortKey === 'name' ? sortOrder : undefined,
      },
      {
        title: '治療院',
        key: 'clinic_name',
        dataIndex: 'clinic_name',
        sorter: true,
        sortOrder: sortKey === 'clinic_name' ? sortOrder : undefined,
      },
    ],
    [sortKey, sortOrder, canUseSiteTypeFeatures, tenantNameBySiteType]
  );

  return (
    <div className="bg-white rounded-md border">
      <AppTable
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />
      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={onChangePage}
          />
        </div>
      )}
    </div>
  );
};

export default ClinicUsersTableSection;
